import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useConnectedServicesStore } from "../../../store/useConnectedServicesStore";

/**
 * DatabaseSelector component
 * 
 * Dropdown for selecting a database from the selected instance
 */
interface DatabaseSelectorProps {
  isTarget?: boolean;
  label?: string;
}

export const DatabaseSelector: React.FC<DatabaseSelectorProps> = ({
  isTarget = false,
  label = "Database"
}) => {
  const { updateFormField } = useWorkflowStore();
  const { databaseOptions, loadingDatabases, databaseError } = useConnectedServicesStore();

  // Field name based on whether this is for source or target
  const fieldName = isTarget ? "targetDatabaseName" : "databaseName";
  const placeholderText = isTarget ? "Select database" : "Select source database";

  // Handle database selection change
  const handleDatabaseChange = (value: string) => {
    if (
      value === "loading-state" ||
      value === "error-state" ||
      value === "no-databases-state"
    ) {
      return;
    }

    updateFormField(fieldName, value);
  };

  return (
    <div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={fieldName}>{label}</Label>
        <Select onValueChange={handleDatabaseChange}>
          <SelectTrigger id={fieldName} className="w-full">
            <SelectValue placeholder={placeholderText} />
          </SelectTrigger>
          <SelectContent>
            {loadingDatabases ? (
              <SelectItem value="loading-state" disabled>
                Loading databases...
              </SelectItem>
            ) : databaseError ? (
              <SelectItem value="error-state" disabled>
                Error: {databaseError}
              </SelectItem>
            ) : databaseOptions.length > 0 ? (
              databaseOptions.map((database, index) => (
                <SelectItem key={index} value={database}>
                  {database}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="no-databases-state" disabled>
                No databases available
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default DatabaseSelector;
